import React, { lazy, startTransition, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, SpaceBetween } from '@cloudscape-design/components';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Task, TaskType } from '@amzn/taskscheduling-service';
import { Action, Resource } from '@amzn/fae-auth-service';
import { columnDefinitions } from './config/table-config';
import { filteringProperties } from './config/driver-rules-filter-property';
import { Table } from 'src/common/components/Table';
import { getContentDisplayOptions } from 'src/common/utils/table';
import { useNotificationContext } from 'src/common/provider/NotificationProvider';
import { Notification } from 'src/common/constants/Notification';
import { useMetrics } from 'src/common/provider/MetricsProvider';
import { Page } from 'src/common/types/Page';
import { PageAction } from 'src/common/types/PageAction';
import { TaskSchedulingServiceApi } from 'src/api/TaskSchedulingServiceApi';
import QueryKey from 'src/api/QueryKey';
import { getTasksWithExecutionState } from 'src/common/utils/task';
import { TaskWithStatus } from 'src/common/types/TaskWithStatus';
import { AuthServiceApi } from 'src/api/AuthServiceApi';

const DeleteModal = lazy(() => import('src/common/components/DeleteModal'));
const ExecuteDriverModal = lazy(() => import('./ExecuteDriverModal'));

const usePermissionQuery = (action: Action, item?: Task) =>
  useQuery({
    queryKey: [QueryKey.HasPermission, action, item],
    queryFn: () =>
      AuthServiceApi.hasPermissions({
        action,
        resource: Resource.TASK,
        region: item?.input?.driverTaskInput?.region,
        business: item?.input?.driverTaskInput?.business,
        methodology: item?.input?.driverTaskInput?.methodology,
      }),
    enabled: Boolean(item),
  });

const ManageDriverPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addNotification } = useNotificationContext();
  const metrics = useMetrics();
  const columnDef = useMemo(() => columnDefinitions(t), [t]);
  const queryClient = useQueryClient();

  const sortingColumn = columnDef.find((def) => def.id === 'creationDate');

  const [selectedItems, setSelectedItems] = useState<Task[]>([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [executeModal, setExecuteModal] = useState(false);

  const tasksWithLatestExecution = useQuery({
    queryKey: [
      QueryKey.ListTasksWithLatestExecutionState,
      TaskType.FAE_DRIVER_TASK,
    ],
    queryFn: () =>
      TaskSchedulingServiceApi.listTaskWithLatestExecution({
        taskTypes: [TaskType.FAE_DRIVER_TASK],
      }),
  });

  const hasDeletePermission = usePermissionQuery(
    Action.DELETE,
    selectedItems[0],
  );

  const hasUpdatePermission = usePermissionQuery(
    Action.UPDATE,
    selectedItems[0],
  );

  const hasExecutePermission = usePermissionQuery(
    Action.EXECUTE,
    selectedItems[0],
  );

  const tasks = useMemo(
    () =>
      getTasksWithExecutionState(
        tasksWithLatestExecution.data?.taskWithExecutionStateList ?? [],
      ),
    [tasksWithLatestExecution.data],
  );

  const deleteTask = useMutation({
    mutationFn: TaskSchedulingServiceApi.deleteTask,
    onSuccess: () => {
      queryClient.invalidateQueries();
    },
  });

  const handleUpdate = () => {
    navigate(`/driver/update/${selectedItems[0].taskId}`);
  };

  const toggleDeleteModal = () => {
    startTransition(() => setDeleteModal(!deleteModal));
  };

  const toggleExecuteModal = () => {
    startTransition(() => setExecuteModal(!executeModal));
  };

  const navigateToDriverDetails = () => {
    navigate(`/driver/details/${selectedItems[0].taskId}`);
  };

  const handleDelete = async (reason: string) => {
    const startTime = performance.now();
    try {
      await deleteTask.mutateAsync({
        taskId: selectedItems[0].taskId,
        deletionReason: reason,
      });
      addNotification({
        type: 'success',
        content: t(Notification.driver.delete.success),
        dismissible: true,
      });
      metrics.publishCounter(
        `${Page.Driver}.${PageAction.Delete}`,
        PageAction.Success,
        1,
      );
    } catch (error: any) {
      addNotification({
        type: 'error',
        header: t(Notification.driver.delete.error),
        content: error?.message,
        dismissible: true,
      });
      metrics.publishCounter(
        `${Page.Driver}.${PageAction.Delete}`,
        PageAction.Failure,
        1,
      );
    }
    const endTime = performance.now();
    metrics.publishTime(
      `${Page.Driver}.${PageAction.Delete}`,
      endTime - startTime,
    );
    metrics.publishCounter(Page.Driver, PageAction.Delete, 1);
    toggleDeleteModal();
  };

  return (
    <div data-testid="manage-driver-page">
      {deleteModal && (
        <DeleteModal
          header={t('delete_driver')}
          name={selectedItems[0].name}
          visible={deleteModal}
          onClose={toggleDeleteModal}
          onDelete={handleDelete}
        />
      )}
      {executeModal && (
        <ExecuteDriverModal
          visible={executeModal}
          onClose={toggleExecuteModal}
          driver={selectedItems[0]}
        />
      )}
      <Table<TaskWithStatus>
        variant="full-page"
        loading={tasksWithLatestExecution.isFetching}
        trackBy="taskId"
        testid="manage-driver-table"
        data={tasks}
        header={t('manage_driver')}
        columnDefinitions={columnDef}
        contentDisplayOptions={getContentDisplayOptions(columnDef)}
        selectedItems={selectedItems}
        onSelectionChange={setSelectedItems}
        pagination={true}
        preferences={true}
        empty={{
          title: t('empty_driver'),
          description: t('empty_driver_description'),
        }}
        selectionType="single"
        filteringProperties={filteringProperties(t)}
        sortingState={
          sortingColumn && {
            sortingColumn,
            isDescending: true,
          }
        }
        buttons={
          <SpaceBetween direction="horizontal" size="s">
            <Button
              disabled={
                !selectedItems.length || !hasUpdatePermission.data?.isAuthorized
              }
              data-testid="update-driver"
              onClick={handleUpdate}
            >
              {t('update')}
            </Button>
            <Button
              disabled={
                !selectedItems.length || !hasDeletePermission.data?.isAuthorized
              }
              data-testid="delete-driver"
              onClick={toggleDeleteModal}
            >
              {t('delete')}
            </Button>
            <Button
              disabled={!selectedItems.length}
              data-testid="driver-history"
              onClick={navigateToDriverDetails}
            >
              {t('execution_details')}
            </Button>
            <Button
              disabled={
                !selectedItems.length ||
                !hasExecutePermission.data?.isAuthorized
              }
              data-testid="execute-driver"
              onClick={toggleExecuteModal}
              variant="primary"
            >
              {t('execute')}
            </Button>
          </SpaceBetween>
        }
      />
    </div>
  );
};

export default ManageDriverPage;
